import * as types from 'constants/ActionTypes';
import * as actionTypes from 'modules/interventions/actionTypes';
import * as stepTypes from 'modules/steps/actionTypes';
import { createEntityAdapter, createReducer } from 'utils/ReduxUtils';

/** @typedef {import('utils/ReduxUtils').EntitySlice<import('modules/steps/types/step').Step>} StepDataEntity */

/** @type {import('utils/ReduxUtils').EntityAdapter<import('modules/steps/types/step').Step>} */
const stepAdapter = createEntityAdapter({ selectId: ({ id }) => id });

/**
 * @param {StepDataEntity} state
 * @param {{payload: {content?: Array}}} action
 * @returns {StepDataEntity}
 */
const updateAllFromInterventions = (state, { payload: { content: interventions } }) => {
  const steps = interventions.reduce((acc, { step }) => {
    if (step) {
      acc.push(step);
    }
    return acc;
  }, []);

  return stepAdapter.updateMany(state, steps);
};

/**
 * @param {StepDataEntity} state
 * @param {{payload: {step?: import('modules/steps/types/step').Step, dealer?: import('modules/dealers/_types/projections/detailed-dealer').DetailedDealer}}} action
 * @returns {StepDataEntity}
 */
const updateFromIntervention = (state, { payload }) => {
  const { step } = payload;
  if (step) {
    return stepAdapter.updateOne(state, step);
  }
  return state;
};

/**
 * @param {StepDataEntity} state
 * @param {{payload: import('modules/dealers/_types/projections/detailed-dealer').DetailedDealer}} action
 * @returns {StepDataEntity}
 */
const updateAllFromDealer = (state, { payload: dealer }) => {
  if (dealer.config?.steps) {
    const content = Object.values(dealer.config.steps).flatMap(({ steps = [] }) => steps);
    return stepAdapter.addMany(state, content);
  }
  return state;
};

/**
 * @param {StepDataEntity} state
 * @param {import('modules/dealers/types').GenericDealersActionPage} action
 * @returns {StepDataEntity}
 */
const updateAllFromDealers = (state, { payload }) => {
  if (payload.content.length) {
    // Collect all steps from all dealers
    const allSteps = payload.content.flatMap(dealer => {
      if (dealer.config?.steps) {
        return Object.values(dealer.config.steps).flatMap(({ steps = [] }) => steps);
      }
      return [];
    });

    // Replace all steps with collected steps from all dealers
    return stepAdapter.addMany(state, allSteps);
  }
  return state;
};

/**
 * @param {StepDataEntity} state
 * @param {{payload: {stepId: string}}} action
 * @returns {StepDataEntity}
 */
const removeStep = (state, { payload: { stepId } }) => stepAdapter.removeOne(state, stepId);

/**
 * @param {StepDataEntity} state
 * @param {{payload: {step: import('modules/steps/types/step').Step}}} action
 * @returns {StepDataEntity}
 */
const updateStep = (state, { payload: { step } }) => stepAdapter.updateOne(state, step);

const addStepsOnActivated = (state, { payload: { steps = [] } }) => stepAdapter.addMany(state, steps);

export default createReducer(stepAdapter.getInitialState(), {
  [actionTypes.FETCH_INTERVENTION_SUCCESS]: updateFromIntervention,
  [actionTypes.FETCH_INTERVENTIONS_SUCCESS]: updateAllFromInterventions,
  [types.FETCH_DEALER_SUCCESS]: updateAllFromDealer,
  [types.UPDATE_DEALER_SUCCESS]: updateAllFromDealer,
  [types.FETCH_DEALERS_SUCCESS]: updateAllFromDealers,

  [stepTypes.CREATE_STEP_SUCCESS]: updateStep,
  [stepTypes.UPDATE_STEP_SUCCESS]: updateStep,
  [stepTypes.DELETE_STEP_SUCCESS]: removeStep,
  [stepTypes.PATCH_STEPS_MODE_SUCCESS]: addStepsOnActivated,
});
